import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useTheme } from '../../context/ThemeContext';
import {
  Scale, Users, ArrowRight, Search, ChevronDown, ChevronUp,
  Zap, TrendingUp, TrendingDown, Target, AlertTriangle,
  Plus, ArrowLeftRight, Award, BarChart3, MessageSquare,
  Calendar, DollarSign, Briefcase, Layers, Star, EyeOff, Check
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, RadarChart, Radar, PolarGrid,
  PolarAngleAxis, PolarRadiusAxis
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WorkloadDecisionAgent = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const [managers, setManagers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('overview'); // overview, reassign, hypothetical
  const [sortField, setSortField] = useState('totalWorkloadCost');
  const [sortDir, setSortDir] = useState('desc');
  const [search, setSearch] = useState('');

  // Reassign state
  const [selectedPlan, setSelectedPlan] = useState('');
  const [recommendations, setRecommendations] = useState(null);
  const [recLoading, setRecLoading] = useState(false);
  const [planInfo, setPlanInfo] = useState(null);
  const [transferResult, setTransferResult] = useState(null);
  const [applying, setApplying] = useState(false);
  const [appliedManager, setAppliedManager] = useState(null);

  // Hypothetical state
  const [hypo, setHypo] = useState({ estimatedRevenue: 50000, estimatedMessages: 200, estimatedMeetings: 4, estimatedLots: 30 });
  const [hypoRecs, setHypoRecs] = useState(null);
  const [hypoLoading, setHypoLoading] = useState(false);

  // Exclusions state — user-managed list of managers who never appear as a
  // candidate in the recommendation engine.  Persisted in the backend.
  const [excludedList, setExcludedList] = useState([]);
  const [builtInExcluded, setBuiltInExcluded] = useState([]);
  const [savingExclusions, setSavingExclusions] = useState(false);
  const [exclusionsOpen, setExclusionsOpen] = useState(false);
  useEffect(() => {
    axios.get(`${API}/workload-agent/exclusions`)
      .then(res => { setExcludedList(res.data.excluded || []); setBuiltInExcluded(res.data.builtIn || []); })
      .catch(() => {});
  }, []);
  const toggleExclusion = (name) => setExcludedList(list => list.includes(name) ? list.filter(n => n !== name) : [...list, name]);
  const saveExclusions = async () => {
    setSavingExclusions(true);
    try {
      await axios.post(`${API}/workload-agent/exclusions`, { excluded: excludedList });
      // Reset current recommendations so they refresh with the new filter.
      setRecommendations(null);
      setHypoRecs(null);
    } finally {
      setSavingExclusions(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/workload-agent/managers`)
      .then(res => { setManagers(res.data.managers); setSummary(res.data.summary); })
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  // All plans for reassignment dropdown
  const allPlans = useMemo(() => {
    const plans = [];
    managers.forEach(m => {
      m.plans.forEach(p => {
        plans.push({ ...p, manager: m.manager });
      });
    });
    return plans.sort((a, b) => a.planId.localeCompare(b.planId));
  }, [managers]);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const filteredManagers = managers
    .filter(m => m.manager.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      const av = a[sortField], bv = b[sortField];
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

  const getRecommendations = async () => {
    if (!selectedPlan) return;
    setRecLoading(true);
    setRecommendations(null);
    setTransferResult(null);
    try {
      const res = await axios.post(`${API}/workload-agent/recommend`, { planId: selectedPlan });
      setRecommendations(res.data.recommendations);
      setPlanInfo(res.data.plan);
    } catch (e) { console.error(e); }
    finally { setRecLoading(false); }
  };

  const simulateTransfer = async (toManager) => {
    if (!selectedPlan) return;
    try {
      const res = await axios.post(`${API}/workload-agent/simulate-transfer`, { planId: selectedPlan, toManager });
      setTransferResult(res.data);
    } catch (e) { console.error(e); }
  };

  // Persist the reassignment — writes to plan_overrides which is the single
  // source of truth used by every analytics view in the app.
  const applyTransfer = async (toManager) => {
    if (!selectedPlan || !toManager) return;
    setApplying(true);
    try {
      await axios.put(`${API}/plan-config/${encodeURIComponent(selectedPlan)}`, { manager: toManager });
      setAppliedManager(toManager);
      // Refresh the manager overview so the new state shows immediately.
      const res = await axios.get(`${API}/workload-agent/managers`);
      setManagers(res.data.managers);
      setSummary(res.data.summary);
    } catch (e) {
      console.error('Failed to apply reassignment:', e);
    } finally {
      setApplying(false);
    }
  };

  const getHypotheticalRecs = async () => {
    setHypoLoading(true);
    setHypoRecs(null);
    try {
      const res = await axios.post(`${API}/workload-agent/recommend`, { hypothetical: hypo });
      setHypoRecs(res.data);
    } catch (e) { console.error(e); }
    finally { setHypoLoading(false); }
  };

  const fmt = (v) => {
    if (v === undefined || v === null) return '-';
    if (Math.abs(v) >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
    if (Math.abs(v) >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
    return `$${v.toFixed(0)}`;
  };

  const SortHeader = ({ field, label, className = '' }) => (
    <th
      onClick={() => handleSort(field)}
      className={`py-3 px-3 text-left text-[11px] font-semibold uppercase tracking-wider cursor-pointer select-none whitespace-nowrap
        ${isDark ? 'text-slate-500 hover:text-slate-300' : 'text-slate-400 hover:text-slate-600'} ${className}`}
    >
      <span className="flex items-center gap-1">
        {label}
        {sortField === field && (sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
      </span>
    </th>
  );

  const ScoreBadge = ({ score, rank }) => {
    const color = score >= 70 ? 'emerald' : score >= 45 ? 'amber' : 'red';
    return (
      <div className="flex items-center gap-2">
        {rank && (
          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold
            ${rank === 1 ? 'bg-amber-500/20 text-amber-500' : rank === 2 ? 'bg-slate-300/20 text-slate-400' : rank === 3 ? 'bg-orange-600/20 text-orange-500' : isDark ? 'bg-white/5 text-slate-500' : 'bg-slate-100 text-slate-400'}`}>
            {rank <= 3 ? <Star className="w-3 h-3" /> : rank}
          </span>
        )}
        {!rank && (
          <div className={`px-2 py-1 rounded-lg text-xs font-bold bg-${color}-500/10 text-${color}-500`}>
            {score.toFixed(0)}
          </div>
        )}
      </div>
    );
  };

  // Everperform pulse chip inside the recommendation table.  Warns the user
  // when the AI is about to push more work TO someone whose wellbeing is
  // already falling — the scoring function has already docked their score,
  // but showing the pulse here explains *why*.
  const RecommendationPulseChip = ({ pulse }) => {
    if (!pulse || pulse.band === 'unknown' || pulse.compositeScore == null) {
      return <span className="text-xs text-slate-400">—</span>;
    }
    const styleMap = {
      critical: 'bg-rose-500/15 text-rose-500 border border-rose-500/30',
      watch:    'bg-amber-500/15 text-amber-500 border border-amber-500/30',
      fine:     'bg-emerald-500/15 text-emerald-500 border border-emerald-500/30',
    };
    const cls = styleMap[pulse.band] || styleMap.fine;
    const tip = [
      `${pulse.label} · composite ${Math.round(pulse.compositeScore)}/100`,
      pulse.compositeVariance != null ? `${pulse.compositeVariance > 0 ? '+' : ''}${Math.round(pulse.compositeVariance)} vs prev month` : null,
      ...(pulse.triggers || []),
      pulse.band !== 'fine' ? '⚠︎ Recommendation score reduced due to pulse signal' : null,
    ].filter(Boolean).join('\n');
    return (
      <span
        title={tip}
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold whitespace-nowrap ${cls}`}
      >
        {Math.round(pulse.compositeScore)}
        {pulse.band !== 'fine' && (
          <span className="text-[9px] uppercase tracking-wider opacity-90">{pulse.band}</span>
        )}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="w-8 h-8 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="workload-decision-agent">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-[10px] p-6 bg-white border border-[#E5E5E5]" style={{boxShadow: 'var(--card-shadow)'}}>
        <div className="relative">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-[#171717] flex items-center justify-center">
              <Scale className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-[#171717] font-heading tracking-tight">Workload Decision Agent</h1>
              <p className="text-sm text-[#737373]">Smart workload balancing and plan assignment recommendations</p>
            </div>
          </div>

          {summary && (
            <div className="flex flex-wrap gap-3 mt-4">
              {[
                { label: 'Managers', value: summary.totalManagers, icon: Users },
                { label: 'Total Plans', value: summary.totalPlans, icon: Briefcase },
                { label: 'Avg Plans/Mgr', value: summary.avgPlansPerManager, icon: Layers },
                { label: 'Avg Workload', value: fmt(summary.avgWorkloadPerManager), icon: BarChart3 },
              ].map((s) => (
                <div key={s.label} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#F5F5F5] border border-[#E5E5E5]">
                  <s.icon className="w-3.5 h-3.5 text-[#A3A3A3]" />
                  <span className="text-[10px] font-medium text-[#737373]">{s.label}:</span>
                  <span className="text-xs font-bold text-[#171717]">{s.value}</span>
                </div>
              ))}
            </div>
          )}

          {/* Mode Tabs */}
          <div className="flex gap-2 mt-5">
            {[
              { key: 'overview', label: 'Manager Overview', icon: Users },
              { key: 'reassign', label: 'Reassign Plan', icon: ArrowLeftRight },
              { key: 'hypothetical', label: 'New Plan', icon: Plus },
            ].map(tab => (
              <button
                key={tab.key}
                data-testid={`mode-tab-${tab.key}`}
                onClick={() => { setMode(tab.key); setTransferResult(null); }}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all
                  ${mode === tab.key
                    ? 'bg-[#171717] border-[#171717] text-white'
                    : 'bg-[#F5F5F5] border-[#E5E5E5] text-[#525252] hover:border-[#D4D4D4]'
                  }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Exclusions strip — hide selected managers from the recommendation
             engine (they won't appear as candidates for reassignment or new
             plans; they still show up on every other page). */}
          <ExclusionsPanel
            open={exclusionsOpen}
            onToggle={() => setExclusionsOpen(v => !v)}
            managers={managers}
            excluded={excludedList}
            builtIn={builtInExcluded}
            onToggleName={toggleExclusion}
            onSave={saveExclusions}
            saving={savingExclusions}
            isDark={isDark}
          />
        </div>
      </div>

      {/* MANAGER OVERVIEW */}
      {mode === 'overview' && (
        <div className="rounded-[10px] border border-[#E5E5E5] overflow-hidden bg-white" style={{boxShadow: 'var(--card-shadow)'}}>
          <div className="p-5 flex items-center justify-between">
            <h2 className="text-[15px] font-bold flex items-center gap-2 text-[#171717] font-heading">
              <BarChart3 className="w-5 h-5 text-[#525252]" /> Manager Workload Distribution
            </h2>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]" />
              <input
                data-testid="manager-search-input"
                type="text" placeholder="Search managers..."
                className="pl-9 pr-4 py-2 text-sm border border-[#E5E5E5] rounded-lg w-56 bg-[#F5F5F5] text-[#171717] placeholder-[#A3A3A3] focus:outline-none focus:border-[#171717]"
                value={search} onChange={e => setSearch(e.target.value)}
              />
            </div>
          </div>

          {/* Chart */}
          <div className="px-5 pb-4">
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={filteredManagers.slice(0, 20).map(m => ({
                  name: m.manager.length > 12 ? m.manager.substring(0, 12) + '..' : m.manager,
                  plans: m.planCount,
                  workload: m.totalWorkloadCost,
                  margin: m.marginPct,
                }))} margin={{ top: 10, right: 20, left: 10, bottom: 50 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.06)'} />
                  <XAxis dataKey="name" tick={{ fill: isDark ? '#64748b' : '#94a3b8', fontSize: 10 }} angle={-45} textAnchor="end" />
                  <YAxis yAxisId="left" tickFormatter={v => fmt(v)} tick={{ fill: isDark ? '#64748b' : '#94a3b8', fontSize: 10 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fill: isDark ? '#64748b' : '#94a3b8', fontSize: 10 }} domain={[0, 'auto']} />
                  <Tooltip formatter={(v, name) => name === 'workload' ? fmt(v) : v} contentStyle={{ backgroundColor: isDark ? '#1a1a1f' : '#fff', border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0', borderRadius: '8px', fontSize: 12 }} />
                  <Bar yAxisId="left" dataKey="workload" name="Workload Cost" radius={[4, 4, 0, 0]}>
                    {filteredManagers.slice(0, 20).map((m) => (
                      <Cell key={m.manager} fill={m.marginPct > 40 ? '#10b981' : m.marginPct > 20 ? '#f59e0b' : '#ef4444'} opacity={0.7} />
                    ))}
                  </Bar>
                  <Bar yAxisId="right" dataKey="plans" name="Plans" fill={isDark ? '#8b5cf6' : '#6366f1'} radius={[4, 4, 0, 0]} opacity={0.5} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="manager-overview-table">
              <thead>
                <tr className="border-t border-b border-[#E5E5E5] bg-[#F8F9FA]">
                  <SortHeader field="manager" label="Manager" />
                  <SortHeader field="planCount" label="Plans" />
                  <SortHeader field="totalRevenue" label="Revenue" />
                  <SortHeader field="totalWorkloadCost" label="Workload" />
                  <SortHeader field="marginPct" label="Margin %" />
                  <SortHeader field="totalMessages" label="Emails" />
                  <SortHeader field="totalMeetings" label="Meetings" />
                  <SortHeader field="totalLots" label="Lots" />
                  <th className="py-3 px-3 text-left text-[10px] font-semibold uppercase tracking-wider text-[#737373]">Persona</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F5F5F5]">
                {filteredManagers.map(m => {
                  const overloaded = m.planCount > (summary?.avgPlansPerManager || 20) * 1.3;
                  const underloaded = m.planCount < (summary?.avgPlansPerManager || 20) * 0.6;
                  return (
                    <tr key={m.manager} className="transition-colors hover:bg-[#FAFAFA]">
                      <td className="py-3 px-3">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-[#171717]">{m.manager}</span>
                          {overloaded && <AlertTriangle className="w-3.5 h-3.5 text-amber-500" title="Overloaded" />}
                          {underloaded && <Zap className="w-3.5 h-3.5 text-blue-400" title="Has capacity" />}
                        </div>
                      </td>
                      <td className="py-3 px-3 text-sm font-mono text-[#525252]">{m.planCount}</td>
                      <td className="py-3 px-3 text-sm font-mono text-[#525252]">{fmt(m.totalRevenue)}</td>
                      <td className="py-3 px-3 text-sm font-mono text-[#525252]">{fmt(m.totalWorkloadCost)}</td>
                      <td className="py-3 px-3">
                        <span className={`text-sm font-bold ${m.marginPct > 40 ? 'text-emerald-500' : m.marginPct > 20 ? 'text-amber-500' : 'text-red-500'}`}>
                          {m.marginPct.toFixed(1)}%
                        </span>
                      </td>
                      <td className="py-3 px-3 text-sm font-mono text-[#525252]">{m.totalMessages.toLocaleString()}</td>
                      <td className="py-3 px-3 text-sm font-mono text-[#525252]">{m.totalMeetings}</td>
                      <td className="py-3 px-3 text-sm font-mono text-[#525252]">{m.totalLots.toLocaleString()}</td>
                      <td className="py-3 px-3">
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#F5F5F5] text-[#525252]">{m.dominantPersona}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* REASSIGN PLAN */}
      {mode === 'reassign' && (
        <div className="space-y-6">
          <div className="rounded-[10px] border border-[#E5E5E5] p-6 bg-white" style={{boxShadow: 'var(--card-shadow)'}}>
            <h2 className="text-[15px] font-bold mb-3 flex items-center gap-2 text-[#171717] font-heading">
              <ArrowLeftRight className="w-5 h-5 text-[#525252]" /> Reassign Existing Plan
            </h2>
            <p className="text-sm mb-4 text-[#737373]">
              Select a plan to see which manager is the best fit based on current workload distribution.
            </p>
            <div className="flex items-end gap-3">
              <div className="flex-1 max-w-md">
                <label className="text-[10px] font-semibold uppercase tracking-wider block mb-1.5 text-[#737373]">Select Plan</label>
                <select
                  data-testid="reassign-plan-select"
                  value={selectedPlan}
                  onChange={e => { setSelectedPlan(e.target.value); setRecommendations(null); setTransferResult(null); setAppliedManager(null); }}
                  className="w-full px-3 py-2.5 rounded-lg border border-[#E5E5E5] text-sm bg-[#F5F5F5] text-[#171717] focus:outline-none focus:border-[#171717]"
                >
                  <option value="">-- Choose a plan --</option>
                  {allPlans.map(p => (
                    <option key={p.planId} value={p.planId}>
                      {p.planId} {p.propertyName ? `- ${p.propertyName}` : ''} ({p.manager}) [{fmt(p.revenue)}]
                    </option>
                  ))}
                </select>
              </div>
              <button
                data-testid="get-recommendations-btn"
                onClick={getRecommendations}
                disabled={!selectedPlan || recLoading}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all
                  ${selectedPlan ? 'bg-[#171717] text-white hover:bg-[#2a2a2a]' : 'opacity-40 cursor-not-allowed bg-[#E5E5E5] text-[#A3A3A3]'}`}
              >
                {recLoading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Target className="w-4 h-4" />}
                Get Recommendations
              </button>
            </div>
          </div>

          {/* Plan Info + Recommendations */}
          {recommendations && planInfo && (
            <>
              {/* Plan details card */}
              <div className={`rounded-2xl border p-5 ${isDark ? 'bg-blue-500/5 border-blue-500/10' : 'bg-blue-50 border-blue-200'}`}>
                <div className="flex items-center gap-2 mb-3">
                  <Briefcase className="w-4 h-4 text-blue-500" />
                  <span className={`text-sm font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Plan {planInfo.planId}</span>
                  {planInfo.propertyName && <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>({planInfo.propertyName})</span>}
                </div>
                <div className="flex flex-wrap gap-4">
                  {[
                    { label: 'Current Manager', value: planInfo.currentManager },
                    { label: 'Revenue', value: fmt(planInfo.revenue) },
                    { label: 'Workload', value: fmt(planInfo.workloadCost) },
                    { label: 'Emails', value: planInfo.messagesReceived },
                    { label: 'Meetings', value: planInfo.totalMeetings },
                    { label: 'Persona', value: planInfo.persona },
                  ].map((item) => (
                    <div key={item.label} className="flex flex-col">
                      <span className={`text-[10px] font-semibold uppercase ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>{item.label}</span>
                      <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Ranked recommendations */}
              <div className={`rounded-2xl border overflow-hidden ${isDark ? 'bg-[#0d0d0f]/50 border-white/5' : 'bg-white border-slate-200'}`}>
                <div className="p-5">
                  <h3 className={`text-lg font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    <Award className="w-5 h-5 text-amber-500" /> Ranked Recommendations
                  </h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="recommendations-table">
                    <thead>
                      <tr className={`border-t border-b ${isDark ? 'border-white/5 bg-white/[0.02]' : 'border-slate-100 bg-slate-50'}`}>
                        <th className={`py-3 px-3 text-left text-[11px] font-semibold uppercase tracking-wider ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Rank</th>
                        <th className={`py-3 px-3 text-left text-[11px] font-semibold uppercase tracking-wider ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Manager</th>
                        <th className={`py-3 px-3 text-left text-[11px] font-semibold uppercase tracking-wider ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Score</th>
                        <th className={`py-3 px-3 text-left text-[11px] font-semibold uppercase tracking-wider ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Pulse</th>
                        <th className={`py-3 px-3 text-left text-[11px] font-semibold uppercase tracking-wider ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Cur. Plans</th>
                        <th className={`py-3 px-3 text-left text-[11px] font-semibold uppercase tracking-wider ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Cur. Margin</th>
                        <th className={`py-3 px-3 text-left text-[11px] font-semibold uppercase tracking-wider ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Cur. Msgs</th>
                        <th className={`py-3 px-3 text-left text-[11px] font-semibold uppercase tracking-wider ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>New Margin</th>
                        <th className={`py-3 px-3 text-left text-[11px] font-semibold uppercase tracking-wider ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>WL Increase</th>
                        <th className={`py-3 px-3 text-left text-[11px] font-semibold uppercase tracking-wider ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Action</th>
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${isDark ? 'divide-white/[0.03]' : 'divide-slate-50'}`}>
                      {recommendations.map((r) => (
                        <tr key={r.manager} className={`transition-colors
                          ${r.isCurrentManager ? isDark ? 'bg-yellow-500/5' : 'bg-yellow-50' : isDark ? 'hover:bg-white/[0.02]' : 'hover:bg-slate-50/50'}`}>
                          <td className="py-3 px-3">
                            {r.isCurrentManager ? (
                              <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${isDark ? 'bg-yellow-500/10 text-yellow-500' : 'bg-yellow-100 text-yellow-600'}`}>Current</span>
                            ) : (
                              <ScoreBadge score={0} rank={r.rank} />
                            )}
                          </td>
                          <td className={`py-3 px-3 text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{r.manager}</td>
                          <td className="py-3 px-3"><ScoreBadge score={r.score} /></td>
                          <td className="py-3 px-3"><RecommendationPulseChip pulse={r.pulse} /></td>
                          <td className={`py-3 px-3 text-sm font-mono ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{r.currentStats.planCount}</td>
                          <td className="py-3 px-3">
                            <span className={`text-sm font-bold ${r.currentStats.marginPct > 40 ? 'text-emerald-500' : r.currentStats.marginPct > 20 ? 'text-amber-500' : 'text-red-500'}`}>
                              {r.currentStats.marginPct.toFixed(1)}%
                            </span>
                          </td>
                          <td className={`py-3 px-3 text-sm font-mono ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{r.currentStats.totalMessages.toLocaleString()}</td>
                          <td className="py-3 px-3">
                            <div className="flex items-center gap-1">
                              <span className={`text-sm font-bold ${r.predictedImpact.newMarginPct > 40 ? 'text-emerald-500' : r.predictedImpact.newMarginPct > 20 ? 'text-amber-500' : 'text-red-500'}`}>
                                {r.predictedImpact.newMarginPct.toFixed(1)}%
                              </span>
                              <span className={`text-[10px] ${r.predictedImpact.marginChange >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                ({r.predictedImpact.marginChange >= 0 ? '+' : ''}{r.predictedImpact.marginChange.toFixed(1)})
                              </span>
                            </div>
                          </td>
                          <td className={`py-3 px-3 text-sm font-mono ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                            +{r.predictedImpact.workloadIncreasePct.toFixed(1)}%
                          </td>
                          <td className="py-3 px-3">
                            {!r.isCurrentManager && (
                              <div className="flex items-center gap-1.5">
                                <button
                                  data-testid={`simulate-transfer-${r.manager}`}
                                  onClick={() => simulateTransfer(r.manager)}
                                  className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all
                                    ${isDark ? 'bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 border border-blue-500/20' : 'bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200'}`}
                                >
                                  <ArrowRight className="w-3 h-3" /> Simulate
                                </button>
                                <button
                                  data-testid={`apply-transfer-${r.manager}`}
                                  onClick={() => applyTransfer(r.manager)}
                                  disabled={applying}
                                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {applying && appliedManager === null ? (
                                    <div className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                                  ) : (
                                    <ArrowLeftRight className="w-3 h-3" />
                                  )}
                                  Reassign
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {/* Reassignment applied confirmation */}
          {appliedManager && (
            <div
              data-testid="reassignment-applied-banner"
              className="rounded-[10px] border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3"
            >
              <div className="w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center flex-shrink-0">
                <ArrowLeftRight className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-bold text-emerald-900">
                  Plan {selectedPlan} reassigned to {appliedManager}
                </p>
                <p className="text-xs text-emerald-800/80 mt-0.5">
                  Saved. The new manager is now reflected across Plan Analysis, Manager Profiling, OC Plan Groups and every other view.
                </p>
              </div>
            </div>
          )}

          {/* Transfer Simulation Result */}
          {transferResult && (
            <div data-testid="transfer-result" className={`rounded-2xl border p-6 ${isDark ? 'bg-[#0d0d0f]/50 border-white/5' : 'bg-white border-slate-200'}`}>
              <h3 className={`text-lg font-bold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                <ArrowLeftRight className="w-5 h-5 text-purple-500" /> Transfer Simulation
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* From Manager */}
                <div className={`p-4 rounded-xl border ${isDark ? 'bg-red-500/5 border-red-500/10' : 'bg-red-50 border-red-200'}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingDown className="w-4 h-4 text-red-500" />
                    <span className={`text-sm font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{transferResult.fromManager.name}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${isDark ? 'bg-red-500/10 text-red-400' : 'bg-red-100 text-red-600'}`}>Loses Plan</span>
                  </div>
                  {[
                    { label: 'Plans', before: transferResult.fromManager.before.planCount, after: transferResult.fromManager.after.planCount },
                    { label: 'Revenue', before: fmt(transferResult.fromManager.before.totalRevenue), after: fmt(transferResult.fromManager.after.totalRevenue) },
                    { label: 'Workload', before: fmt(transferResult.fromManager.before.totalWorkloadCost), after: fmt(transferResult.fromManager.after.totalWorkloadCost) },
                    { label: 'Margin', before: `${transferResult.fromManager.before.marginPct.toFixed(1)}%`, after: `${transferResult.fromManager.after.marginPct.toFixed(1)}%` },
                  ].map((row) => (
                    <div key={row.label} className="flex items-center justify-between py-1.5">
                      <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{row.label}</span>
                      <div className="flex items-center gap-2 text-xs font-mono">
                        <span className={isDark ? 'text-slate-500' : 'text-slate-400'}>{row.before}</span>
                        <ArrowRight className="w-3 h-3 text-slate-400" />
                        <span className={isDark ? 'text-white' : 'text-slate-900'}>{row.after}</span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Plan in transit */}
                <div className={`p-4 rounded-xl border flex flex-col items-center justify-center ${isDark ? 'bg-white/[0.02] border-white/5' : 'bg-slate-50 border-slate-200'}`}>
                  <Briefcase className={`w-8 h-8 mb-2 ${isDark ? 'text-slate-500' : 'text-slate-300'}`} />
                  <span className={`text-sm font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Plan {transferResult.plan.planId}</span>
                  {transferResult.plan.propertyName && <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{transferResult.plan.propertyName}</span>}
                  <div className="flex gap-3 mt-2">
                    <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{fmt(transferResult.plan.revenue)}</span>
                    <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{transferResult.plan.messagesReceived} msgs</span>
                  </div>
                </div>

                {/* To Manager */}
                <div className={`p-4 rounded-xl border ${isDark ? 'bg-emerald-500/5 border-emerald-500/10' : 'bg-emerald-50 border-emerald-200'}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingUp className="w-4 h-4 text-emerald-500" />
                    <span className={`text-sm font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{transferResult.toManager.name}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${isDark ? 'bg-emerald-500/10 text-emerald-400' : 'bg-emerald-100 text-emerald-600'}`}>Gains Plan</span>
                  </div>
                  {[
                    { label: 'Plans', before: transferResult.toManager.before.planCount, after: transferResult.toManager.after.planCount },
                    { label: 'Revenue', before: fmt(transferResult.toManager.before.totalRevenue), after: fmt(transferResult.toManager.after.totalRevenue) },
                    { label: 'Workload', before: fmt(transferResult.toManager.before.totalWorkloadCost), after: fmt(transferResult.toManager.after.totalWorkloadCost) },
                    { label: 'Margin', before: `${transferResult.toManager.before.marginPct.toFixed(1)}%`, after: `${transferResult.toManager.after.marginPct.toFixed(1)}%` },
                  ].map((row) => (
                    <div key={row.label} className="flex items-center justify-between py-1.5">
                      <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{row.label}</span>
                      <div className="flex items-center gap-2 text-xs font-mono">
                        <span className={isDark ? 'text-slate-500' : 'text-slate-400'}>{row.before}</span>
                        <ArrowRight className="w-3 h-3 text-slate-400" />
                        <span className={isDark ? 'text-white' : 'text-slate-900'}>{row.after}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Apply this reassignment */}
              <div className="mt-5 pt-5 border-t border-[#F0F0F0] flex items-center justify-between gap-3 flex-wrap">
                <p className="text-xs text-[#737373]">
                  Happy with the simulation? Apply this transfer to save the new manager assignment across the platform.
                </p>
                <button
                  data-testid="apply-simulation-btn"
                  onClick={() => applyTransfer(transferResult.toManager.name)}
                  disabled={applying}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {applying ? (
                    <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  ) : (
                    <ArrowLeftRight className="w-4 h-4" />
                  )}
                  Apply Reassignment to {transferResult.toManager.name}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* HYPOTHETICAL NEW PLAN */}
      {mode === 'hypothetical' && (
        <div className="space-y-6">
          <div className={`rounded-2xl border p-6 ${isDark ? 'bg-[#0d0d0f]/50 border-white/5' : 'bg-white border-slate-200'}`}>
            <h2 className={`text-lg font-bold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              <Plus className="w-5 h-5 text-emerald-500" /> Hypothetical New Plan
            </h2>
            <p className={`text-sm mb-5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              Enter estimated metrics for a new plan to see which manager is the best fit.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { key: 'estimatedRevenue', label: 'Est. Revenue ($)', icon: DollarSign, step: 1000 },
                { key: 'estimatedMessages', label: 'Est. Messages/Month', icon: MessageSquare, step: 10 },
                { key: 'estimatedMeetings', label: 'Est. Meetings/Year', icon: Calendar, step: 1 },
                { key: 'estimatedLots', label: 'Est. Occupiable Lots', icon: Layers, step: 5 },
              ].map(f => (
                <div key={f.key}>
                  <label className={`text-xs font-semibold uppercase tracking-wider flex items-center gap-1 mb-1.5 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                    <f.icon className="w-3 h-3" /> {f.label}
                  </label>
                  <input
                    data-testid={`hypo-${f.key}`}
                    type="number"
                    value={hypo[f.key]}
                    onChange={e => setHypo(prev => ({ ...prev, [f.key]: parseFloat(e.target.value) || 0 }))}
                    step={f.step}
                    className={`w-full px-3 py-2.5 rounded-xl border text-sm
                      ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-slate-50 border-slate-200 text-slate-900'}`}
                  />
                </div>
              ))}
            </div>
            <div className="mt-5">
              <button
                data-testid="get-hypo-recommendations-btn"
                onClick={getHypotheticalRecs}
                disabled={hypoLoading}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold bg-emerald-500 text-white hover:bg-emerald-600 transition-all"
              >
                {hypoLoading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Target className="w-4 h-4" />}
                Find Best Manager
              </button>
            </div>
          </div>

          {/* Hypothetical Recommendations */}
          {hypoRecs && (
            <div className={`rounded-2xl border overflow-hidden ${isDark ? 'bg-[#0d0d0f]/50 border-white/5' : 'bg-white border-slate-200'}`}>
              <div className="p-5">
                <div className="flex items-center justify-between">
                  <h3 className={`text-lg font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    <Award className="w-5 h-5 text-amber-500" /> Ranked Recommendations
                  </h3>
                  {hypoRecs.bestMatch && (
                    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-xl ${isDark ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-emerald-50 border border-emerald-200'}`}>
                      <Star className="w-4 h-4 text-amber-500" />
                      <span className={`text-sm font-semibold ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}>Best: {hypoRecs.bestMatch}</span>
                    </div>
                  )}
                </div>
                <div className={`flex flex-wrap gap-3 mt-3 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                  <span className="text-xs">Est. Revenue: {fmt(hypoRecs.plan.revenue)}</span>
                  <span className="text-xs">Est. Workload: {fmt(hypoRecs.plan.workloadCost)}</span>
                  <span className="text-xs">Est. Messages: {hypoRecs.plan.messagesReceived}</span>
                  <span className="text-xs">Est. Meetings: {hypoRecs.plan.totalMeetings}</span>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="hypo-recommendations-table">
                  <thead>
                    <tr className={`border-t border-b ${isDark ? 'border-white/5 bg-white/[0.02]' : 'border-slate-100 bg-slate-50'}`}>
                      <th className={`py-3 px-3 text-left text-[11px] font-semibold uppercase tracking-wider ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Rank</th>
                      <th className={`py-3 px-3 text-left text-[11px] font-semibold uppercase tracking-wider ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Manager</th>
                      <th className={`py-3 px-3 text-left text-[11px] font-semibold uppercase tracking-wider ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Score</th>
                      <th className={`py-3 px-3 text-left text-[11px] font-semibold uppercase tracking-wider ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Pulse</th>
                      <th className={`py-3 px-3 text-left text-[11px] font-semibold uppercase tracking-wider ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Cur. Plans</th>
                      <th className={`py-3 px-3 text-left text-[11px] font-semibold uppercase tracking-wider ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Cur. Workload</th>
                      <th className={`py-3 px-3 text-left text-[11px] font-semibold uppercase tracking-wider ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Cur. Margin</th>
                      <th className={`py-3 px-3 text-left text-[11px] font-semibold uppercase tracking-wider ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>New Margin</th>
                      <th className={`py-3 px-3 text-left text-[11px] font-semibold uppercase tracking-wider ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>WL Increase</th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${isDark ? 'divide-white/[0.03]' : 'divide-slate-50'}`}>
                    {hypoRecs.recommendations.map((r) => (
                      <tr key={r.manager} className={`transition-colors ${r.rank === 1 ? isDark ? 'bg-emerald-500/5' : 'bg-emerald-50' : isDark ? 'hover:bg-white/[0.02]' : 'hover:bg-slate-50/50'}`}>
                        <td className="py-3 px-3"><ScoreBadge score={0} rank={r.rank} /></td>
                        <td className="py-3 px-3">
                          <div className="flex items-center gap-2">
                            <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{r.manager}</span>
                            {r.rank === 1 && <span className={`text-[10px] px-1.5 py-0.5 rounded-lg font-bold ${isDark ? 'bg-emerald-500/10 text-emerald-400' : 'bg-emerald-100 text-emerald-600'}`}>RECOMMENDED</span>}
                          </div>
                        </td>
                        <td className="py-3 px-3"><ScoreBadge score={r.score} /></td>
                        <td className="py-3 px-3"><RecommendationPulseChip pulse={r.pulse} /></td>
                        <td className={`py-3 px-3 text-sm font-mono ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{r.currentStats.planCount}</td>
                        <td className={`py-3 px-3 text-sm font-mono ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{fmt(r.currentStats.totalWorkloadCost)}</td>
                        <td className="py-3 px-3">
                          <span className={`text-sm font-bold ${r.currentStats.marginPct > 40 ? 'text-emerald-500' : r.currentStats.marginPct > 20 ? 'text-amber-500' : 'text-red-500'}`}>
                            {r.currentStats.marginPct.toFixed(1)}%
                          </span>
                        </td>
                        <td className="py-3 px-3">
                          <div className="flex items-center gap-1">
                            <span className={`text-sm font-bold ${r.predictedImpact.newMarginPct > 40 ? 'text-emerald-500' : r.predictedImpact.newMarginPct > 20 ? 'text-amber-500' : 'text-red-500'}`}>
                              {r.predictedImpact.newMarginPct.toFixed(1)}%
                            </span>
                            <span className={`text-[10px] ${r.predictedImpact.marginChange >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                              ({r.predictedImpact.marginChange >= 0 ? '+' : ''}{r.predictedImpact.marginChange.toFixed(1)})
                            </span>
                          </div>
                        </td>
                        <td className={`py-3 px-3 text-sm font-mono ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                          +{r.predictedImpact.workloadIncreasePct.toFixed(1)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WorkloadDecisionAgent;

// ── Exclusions Panel ────────────────────────────────────────────────────────
// Collapsible strip that lets the user hide selected managers from the
// Workload Decision Agent's recommendation lists.  Team-Leader bulk-select is
// derived from the `teamLeader` column on each manager row — anyone acting as
// a TL for someone else is included in the quick-select.
const ExclusionsPanel = ({ open, onToggle, managers, excluded, builtIn, onToggleName, onSave, saving, isDark }) => {
  const teamLeaderNames = React.useMemo(() => {
    const set = new Set();
    managers.forEach(m => { if (m.teamLeader) set.add(m.teamLeader); });
    return Array.from(set).sort();
  }, [managers]);

  // Include TLs who don't hold their own plans (e.g., Gregor Evans) so they
  // can still be excluded — otherwise they'd be invisible to this panel.
  const allNames = React.useMemo(
    () => Array.from(new Set([
      ...managers.map(m => m.manager),
      ...teamLeaderNames,
      ...(excluded || []),
      ...(builtIn || []),
    ])).sort(),
    [managers, teamLeaderNames, excluded, builtIn],
  );

  const excludedSet = React.useMemo(() => new Set(excluded), [excluded]);
  const builtInSet = React.useMemo(() => new Set(builtIn), [builtIn]);
  const teamLeadersAllExcluded = teamLeaderNames.length > 0 && teamLeaderNames.every(n => excludedSet.has(n));

  const bulkToggleTeamLeaders = () => {
    if (teamLeadersAllExcluded) {
      teamLeaderNames.forEach(n => { if (excludedSet.has(n)) onToggleName(n); });
    } else {
      teamLeaderNames.forEach(n => { if (!excludedSet.has(n)) onToggleName(n); });
    }
  };

  const totalHidden = excluded.length + builtIn.length;

  return (
    <div className="mt-4 border-t border-[#E5E5E5] pt-4">
      <button
        data-testid="wda-exclusions-toggle"
        onClick={onToggle}
        className={`w-full flex items-center gap-2 text-sm font-medium ${isDark ? 'text-slate-300 hover:text-white' : 'text-[#525252] hover:text-[#171717]'} transition-colors`}
      >
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        <EyeOff className="w-4 h-4" />
        <span>Excluded from recommendations</span>
        <span className={`ml-1 text-[11px] px-2 py-0.5 rounded-full font-bold ${totalHidden ? 'bg-amber-100 text-amber-800' : 'bg-[#F5F5F5] text-[#737373]'}`}>
          {totalHidden}
        </span>
        <span className="ml-auto text-[11px] text-[#A3A3A3] font-normal">Hidden only from this tool — every other page still shows them.</span>
      </button>

      {open && (
        <div data-testid="wda-exclusions-panel" className="mt-4 rounded-[10px] border border-[#E5E5E5] bg-[#FAFAFA] p-4">
          <div className="flex items-center gap-2 flex-wrap mb-3">
            <button
              data-testid="wda-exclusions-team-leaders"
              onClick={bulkToggleTeamLeaders}
              className={`px-3 py-1.5 rounded-full text-[11px] font-semibold border transition-colors
                ${teamLeadersAllExcluded ? 'bg-[#171717] text-white border-[#171717]' : 'bg-white text-[#525252] border-[#E5E5E5] hover:border-[#171717]'}`}
            >
              {teamLeadersAllExcluded ? <Check className="w-3 h-3 inline mr-1" /> : null}
              Exclude all Team Leaders ({teamLeaderNames.length})
            </button>
            <button
              onClick={onSave}
              disabled={saving}
              data-testid="wda-exclusions-save"
              className="ml-auto px-3 py-1.5 rounded-full text-[11px] font-semibold bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-1.5">
            {allNames.map(name => {
              const isExcluded = excludedSet.has(name);
              const isBuiltIn = builtInSet.has(name);
              const isTL = teamLeaderNames.includes(name);
              const disabled = isBuiltIn;
              return (
                <label
                  key={name}
                  data-testid={`wda-excl-row-${name.replace(/\s+/g, '-')}`}
                  className={`flex items-center gap-2 text-[12px] py-1 px-1.5 rounded-md ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:bg-white'}`}
                  title={isBuiltIn ? 'Built-in exclusion (edit backend to change)' : ''}
                >
                  <input
                    type="checkbox"
                    checked={isExcluded || isBuiltIn}
                    disabled={disabled}
                    onChange={() => !disabled && onToggleName(name)}
                    className="w-3.5 h-3.5 accent-[#171717]"
                  />
                  <span className="text-[#171717] truncate">{name}</span>
                  {isTL && <span className="text-[9px] font-bold uppercase tracking-wider text-[#A3A3A3]">TL</span>}
                  {isBuiltIn && <span className="text-[9px] font-bold uppercase tracking-wider text-[#A3A3A3]">Built-in</span>}
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
